import React, { useState, useEffect } from 'react';
import { UserCheck, Loader2, RefreshCw, Crown, Search, Clock } from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../../Toast';

interface ActiveUser {
    id: string;
    full_name?: string;
    email?: string;
    plan_type?: string;
    subscription_status?: string;
    subscription_expires_at?: string;
    created_at?: string;
}

function daysUntil(dateStr?: string): number | null {
    if (!dateStr) return null;
    const diff = new Date(dateStr).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr?: string) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export const ActiveUsers: React.FC = () => {
    const [users, setUsers] = useState<ActiveUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/banana/active-users');
            setUsers(res.data.users || []);
        } catch {
            setToast({ msg: 'Erro ao carregar usuários ativos', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = users.filter(u => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (u.full_name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q);
    });

    return (
        <div className="p-6 max-w-5xl">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <UserCheck className="text-orange-500" size={24} /> Usuários Ativos
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Assinantes com plano ativo na plataforma</p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white text-sm transition-all disabled:opacity-50"
                >
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    Atualizar
                </button>
            </div>

            {/* Counter card */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-6 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Crown size={26} className="text-orange-400" />
                </div>
                <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Total de assinantes ativos</p>
                    {loading ? (
                        <div className="h-8 w-16 bg-white/5 rounded animate-pulse" />
                    ) : (
                        <p className="text-4xl font-bold text-white">{users.length}</p>
                    )}
                </div>
            </div>

            {/* Tabela */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-12 flex items-center justify-center gap-2 text-gray-500">
                        <Loader2 size={20} className="animate-spin" /> Carregando...
                    </div>
                ) : users.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">Nenhum usuário com assinatura ativa.</div>
                ) : (
                    <>
                        <div className="p-4 border-b border-white/5">
                            <div className="relative w-full max-w-xs">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={15} />
                                <input
                                    type="text"
                                    placeholder="Buscar nome ou e-mail..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-9 pr-4 text-white text-sm focus:border-orange-500/50 outline-none"
                                />
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5">
                                    <tr>
                                        <th className="p-4 font-normal uppercase tracking-wider">Usuário</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Plano</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Expira em</th>
                                        <th className="p-4 font-normal uppercase tracking-wider">Dias restantes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(user => {
                                        const days = daysUntil(user.subscription_expires_at);
                                        const isExpiringSoon = days !== null && days <= 7;
                                        return (
                                            <tr key={user.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                                                <td className="p-4">
                                                    <div className="font-medium text-white">{user.full_name || '—'}</div>
                                                    <div className="text-xs text-gray-500">{user.email || '—'}</div>
                                                </td>
                                                <td className="p-4">
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase">
                                                        <Crown size={10} />
                                                        {user.plan_type || 'pro'}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-300">
                                                    {formatDate(user.subscription_expires_at)}
                                                </td>
                                                <td className="p-4">
                                                    {days === null ? (
                                                        <span className="text-gray-500">—</span>
                                                    ) : (
                                                        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${isExpiringSoon ? 'text-red-400' : 'text-green-400'}`}>
                                                            <Clock size={12} />
                                                            {days > 0 ? `${days}d` : 'Expirado'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {search.trim() && filtered.length === 0 && (
                            <p className="text-center text-gray-500 text-sm py-4">Nenhum resultado para &quot;{search}&quot;</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
